import React, { useState, useEffect } from 'react';
import { Search, Loader } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

/**
 * ExamenesEstudiante - Módulo informativo de exámenes programados
 * Datos proporcionados por el módulo de docente
 */
const TareasEstudiante = () => {
  const [examenes, setExamenes] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadExamenes();
  }, []);

  const loadExamenes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const response = await fetch(`${API_URL}/estudiante/examenes`, { headers });
      if (response.ok) {
        const data = await response.json();
        setExamenes(data.examenes || []);
      } else {
        setExamenes([
          { id_examen: 1, materia: 'HISTORIA DE LA DANZA', docente: 'Lic. María García', tipo_examen: 'Parcial', sala: 'Aula 101', fecha: '2025-01-15', hora: '09:00' },
          { id_examen: 2, materia: 'TÉCNICA DE BALLET', docente: 'Prof. Carlos López', tipo_examen: 'Final', sala: 'Auditorio', fecha: '2025-01-20', hora: '14:00' },
          { id_examen: 3, materia: 'FOLKLORE BOLIVIANO', docente: 'Lic. Ana Pérez', tipo_examen: 'Práctico', sala: 'Sala A', fecha: '2025-01-22', hora: '10:30' },
          { id_examen: 4, materia: 'COREOGRAFÍA I', docente: 'Prof. Juan Mamani', tipo_examen: 'Parcial', sala: 'Aula 203', fecha: '2025-01-25', hora: '16:00' }
        ]);
      }
    } catch (error) {
      console.error('Error loading examenes:', error);
      setExamenes([]);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar por búsqueda
  const examenesFiltrados = examenes.filter(e =>
    e.materia?.toLowerCase().includes(search.toLowerCase()) ||
    e.docente?.toLowerCase().includes(search.toLowerCase()) ||
    e.tipo_examen?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-12 flex flex-col justify-center items-center">
        <Loader className="animate-spin text-blue-600 mb-4" size={40} />
        <p className="text-gray-500 font-medium">Cargando tus evaluaciones...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header con gradiente suave */}
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-gray-800">
            Estudiantes &gt; Evaluaciones Programadas
          </h1>
        </div>


        <div className="relative group">
          <input
            type="text"
            placeholder="Buscar materia o docente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full md:w-80 pl-11 pr-4 py-2.5 bg-white border border-gray-200 rounded-2xl shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none text-sm"
          />
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" size={18} />
        </div>
      </div>

      {/* Tabla Premium */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr className="bg-gray-50/80 border-b border-gray-100">
                <th className="px-3 py-3 text-center font-semibold border">No</th>
                <th className="px-3 py-3 text-center font-semibold border">Materia / Asignatura</th>
                <th className="px-3 py-3 text-center font-semibold border">Docente</th>
                <th className="px-3 py-3 text-center font-semibold border">Tipo</th>
                <th className="px-3 py-3 text-center font-semibold border">Ubicación</th>
                <th className="px-3 py-3 text-center font-semibold border">Fecha y Hora</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {examenesFiltrados.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-20 text-center">
                    <div className="flex flex-col items-center">
                      <div className="bg-blue-50 p-4 rounded-full mb-3 text-blue-500">
                        <Search size={32} />
                      </div>
                      <p className="text-gray-500 font-medium text-lg">No se encontraron evaluaciones</p>
                      <p className="text-gray-400 text-sm mt-1">Verifica que estés inscrito en las materias correspondientes.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                examenesFiltrados.map((examen, idx) => (
                  <tr key={examen.id_examen || idx} className="group hover:bg-blue-50/30 transition-all duration-200">
                    <td className="px-6 py-5 text-sm text-gray-400 font-medium text-center border">{idx + 1}</td>
                    <td className="px-6 py-5 cursor-default border">
                      <div className="flex flex-col border">
                        <span className="text-[13px] font-bold text-gray-900 group-hover:text-blue-700 transition-colors">
                          {examen.materia || examen.asignatura}
                        </span>
                        <span className="text-[11px] text-blue-600 font-semibold tracking-wider">
                          EBID ACADEMIC
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-5 text-sm font-medium text-gray-700 leading-relaxed capitalize border">
                      {examen.docente?.toLowerCase()}
                    </td>
                    <td className="px-6 py-5 text-center border">
                      <span className={`inline-flex px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${examen.tipo_examen?.includes('Final')
                          ? 'bg-purple-50 text-purple-600 border border-purple-100'
                          : 'bg-emerald-50 text-emerald-600 border border-emerald-100'
                        }`}>
                        {examen.tipo_examen || 'Evaluación'}
                      </span>
                    </td>
                    <td className="px-6 py-5 text-center border">
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></span>
                        {examen.sala || examen.aula || 'S/N'}
                      </div>
                    </td>
                    <td className="px-6 py-5 text-center border">
                      <div className="flex flex-col items-center">
                        <span className="text-[13px] font-bold text-gray-900">
                          {examen.fecha ? new Date(examen.fecha).toLocaleDateString('es-BO', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                        </span>
                        <span className="text-[11px] text-gray-500 font-medium">
                          {examen.hora ? examen.hora.substring(0, 5) : '--:--'} HRS
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Nota informativa */}
      <div className="mt-8 p-4 bg-blue-50/50 border border-blue-100 rounded-2xl flex items-start gap-3">
        <div className="mt-0.5 text-blue-500">
          <Loader size={16} className="animate-pulse" />
        </div>
        <p className="text-[11px] text-blue-800/70 leading-relaxed font-medium">
          <strong>Nota Informativa:</strong> Este calendario de evaluaciones se actualiza automáticamente según la programación de los docentes.
          Las evaluaciones son programadas exclusivamente por los docentes - los estudiantes no pueden seleccionar ni crear evaluaciones.
          Si tienes alguna duda sobre una fecha, por favor comunícate directamente con el docente de la asignatura.
        </p>
      </div>
    </div>
  );
};

export default TareasEstudiante;